import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoCollaborators from "config/demo/collaborators.json";
import CollaboratorForm from "components/agro/collaborators/CollaboratorForm";

const Collaborators = () => {
  const [collaborators] = useState(demoCollaborators.slice(0, 10));
  const [collabDetails, setCollabDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      collaborators.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={
                collabDetails?.id === item.id
                  ? strings.collaborators.hide
                  : strings.collaborators.show
              }
              icon={
                collabDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                collabDetails?.id === item.id
                  ? setCollabDetails(null)
                  : setCollabDetails(item);
              }}
            />
            <ActionIcon
              name={strings.collaborators.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.collaborators.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.name,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collaborators, collabDetails?.id]
  );

  const openModal = (action, id = null) =>
    setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "role",
      label: strings.table.headers.role,
      _style: { width: "25%" },
    },
    {
      key: "status",
      label: strings.table.headers.status,
      _style: { width: "15%" },
    },
    {
      key: "startDate",
      label: strings.table.headers.startDate,
      _style: { width: "15%" },
    },
    { key: "actions", label: "", _style: { width: "20%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.collaborators.addCollaborator}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
          />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!collabDetails?.id} style={styles.collapseCard}>
          <CCard key={collabDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{collabDetails?.name}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.name}:</b> {collabDetails?.name}
              </p>

              <p className="text-muted">
                <b>{strings.details.role}:</b> {collabDetails?.role}
              </p>

              <p className="text-muted">
                <b>{strings.details.contractType}:</b>{" "}
                {collabDetails?.contractType}
              </p>

              <p className="text-muted">
                <b>{strings.details.startDate}:</b>{" "}
                {collabDetails?.startDate
                  ? moment(collabDetails.startDate).format("YYYY-MM-DD")
                  : "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.status}:</b> {collabDetails?.status}
              </p>

              <p className="text-muted">
                <b>{strings.details.photo}:</b> {collabDetails?.photo || "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.cv}:</b> {collabDetails?.cv || "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.affiliations}:</b>{" "}
                {(Array.isArray(collabDetails?.affiliations)
                  ? collabDetails.affiliations
                      .map((a) => a.name || JSON.stringify(a))
                      .join(", ")
                  : collabDetails?.affiliations) || "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {collabDetails?.createdAt
                  ? moment(collabDetails.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.itemName,
        })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalForm.visible}
          onClose={() => setModalForm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalForm.action === "create"
                ? strings.modalForm.addTitle
                : strings.modalForm.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <CollaboratorForm
                type={modalForm.action}
                initialValues={
                  modalForm.action === "update"
                    ? collaborators.find((l) => l.id === modalForm.itemId) || {}
                    : {}
                }
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Colaboradores",
    collaborators: {
      addCollaborator: "Agregar Colaborador",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        name: "Nombre",
        role: "Cargo",
        status: "Estado",
        startDate: "Fecha de ingreso",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Colaborador",
      name: "Nombre",
      role: "Cargo",
      contractType: "Tipo de Contrato",
      startDate: "Fecha de Ingreso",
      status: "Estado",
      photo: "Foto",
      cv: "Hoja de Vida",
      affiliations: "Afiliaciones",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar al colaborador {name}?",
    },
    modalForm: {
      addTitle: "Agregar Colaborador",
      updateTitle: "Actualizar Colaborador",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default Collaborators;
